import {
  Onboarding,
  OnboardingPage,
  OnboardingQuestion,
  OnboardingQuestionType,
} from '../generated/gql-types';
import { getApolloClient } from '../lib/getApolloClient';
import { GET_ONBOARDING, GET_ONBOARDING_FOR_USER } from './onboarding.gql';
import { User } from '../generated/gql-types';
import { fetchCurrentUserOnMobile } from '../user/user.action';

type CustomAttributeValueOwner = 'USER' | 'CLIENT';

function normaliseCustomAttributeOwner(
  objectToManipulate: string | null | undefined,
): CustomAttributeValueOwner {
  if ((objectToManipulate || '').toUpperCase() === 'CLIENT') {
    return 'CLIENT';
  }
  return 'USER';
}

function hasDeclaredObjectToManipulate(
  objectToManipulate: string | null | undefined,
): boolean {
  return objectToManipulate != null && String(objectToManipulate).trim() !== '';
}

/**
 * Merges user customAttributes into onboarding questions that map to the user.
 * Skips questions scoped to CLIENT so `getOnboarding` values (from the client
 * record) are not overwritten by the user's customAttributes.
 *
 * Matches mobile save logic: descendants of a customAttributes question inherit
 * storage on user.customAttributes even when they omit objectAttributeToManipulate.
 */
const populateQuestionValueFromCustomAttributes = (
  question: OnboardingQuestion,
  user: User | null,
  inheritedOwner?: CustomAttributeValueOwner,
): void => {
  if (question.type === OnboardingQuestionType.FileUpload) {
    question.childQuestions?.forEach((childQuestion) => {
      populateQuestionValueFromCustomAttributes(childQuestion, user, undefined);
    });
    return;
  }

  const storesViaCustomAttributes =
    question.objectAttributeToManipulate === 'customAttributes' ||
    inheritedOwner !== undefined;

  if (storesViaCustomAttributes && user?.customAttributes) {
    const declared = hasDeclaredObjectToManipulate(question.objectToManipulate)
      ? normaliseCustomAttributeOwner(question.objectToManipulate)
      : undefined;
    const effectiveOwner: CustomAttributeValueOwner =
      declared ?? inheritedOwner ?? 'USER';

    if (effectiveOwner !== 'CLIENT') {
      const customAttribute = user.customAttributes.find(
        (attr) => attr?.ref === question.key,
      );
      if (
        customAttribute?.value != null &&
        String(customAttribute.value).length > 0 &&
        customAttribute.value !== '********'
      ) {
        question.value = String(customAttribute.value);
      }
    }
  }

  const ownerForChildren: CustomAttributeValueOwner | undefined =
    question.objectAttributeToManipulate === 'customAttributes'
      ? hasDeclaredObjectToManipulate(question.objectToManipulate)
        ? normaliseCustomAttributeOwner(question.objectToManipulate)
        : (inheritedOwner ?? 'USER')
      : inheritedOwner;

  if (question.childQuestions) {
    question.childQuestions.forEach((childQuestion) => {
      populateQuestionValueFromCustomAttributes(
        childQuestion,
        user,
        ownerForChildren,
      );
    });
  }
};

/**
 * Populates onboarding questions that store data on the user with values from
 * `user.customAttributes`. Client-scoped custom attribute questions keep the
 * values returned by the API.
 */
const populateOnboardingQuestionsFromCustomAttributes = (
  onboarding: any,
  user: User | null,
): void => {
  if (!onboarding?.onboardingJSON?.pages || !user) {
    return;
  }

  onboarding.onboardingJSON.pages.forEach((page: OnboardingPage) => {
    if (page.questions) {
      page.questions.forEach((question: OnboardingQuestion) => {
        populateQuestionValueFromCustomAttributes(question, user);
      });
    }
  });
};

export const onboardingActions = {
  fetchOnboarding: async () => {
    try {
      const { data } = await getApolloClient().query({ query: GET_ONBOARDING });

      const onboarding = data?.getOnboarding
        ? (JSON.parse(JSON.stringify(data.getOnboarding)) as Onboarding)
        : null;

      if (onboarding?.onboardingJSON?.pages) {
        onboarding.onboardingJSON.pages.sort(
          (a: OnboardingPage, b: OnboardingPage) => Number(a.id) - Number(b.id),
        );
      }

      const currentUser = await fetchCurrentUserOnMobile({ networkOnly: true });
      populateOnboardingQuestionsFromCustomAttributes(onboarding, currentUser);

      return onboarding;
    } catch (error) {
      console.error('Error fetching onboarding:', error);
      return null;
    }
  },

  fetchAndSetOnboarding: async () => {
    try {
      const { data } = await getApolloClient().query({ query: GET_ONBOARDING });

      const onboarding = data?.getOnboarding
        ? (JSON.parse(JSON.stringify(data.getOnboarding)) as Onboarding)
        : null;

      if (onboarding?.onboardingJSON?.pages) {
        onboarding.onboardingJSON.pages.sort(
          (a: OnboardingPage, b: OnboardingPage) => Number(a.id) - Number(b.id),
        );
      }

      const currentUser = await fetchCurrentUserOnMobile({ networkOnly: true });
      populateOnboardingQuestionsFromCustomAttributes(onboarding, currentUser);

      return onboarding;
    } catch (error) {
      console.error('Error fetching onboarding:', error);
      return null;
    }
  },

  fetchOnboardingForUser: async (userId: string): Promise<Onboarding> => {
    try {
      const { data } = await getApolloClient().query({
        query: GET_ONBOARDING_FOR_USER,
        variables: { userId },
      });
      return data?.getOnboardingForUser;
    } catch (error) {
      console.error('Error fetching onboarding for user:', error);
      throw error;
    }
  },
};
